import React, { useCallback, useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { C } from "../theme";

// In-app QR scanner. Uses getUserMedia (the same camera path the app already
// uses for catch video) + jsQR (pure JS) so it works inside the Capacitor
// WebView on Android/iOS and in the PWA without a native scanner plugin.
//
// Props:
//   onResult(text)  — called once with the decoded string, then scanning stops.
//   onClose()       — user dismissed the scanner.
//   title           — optional heading.
//   hint            — optional instruction line.
export default function QrScanner({ onResult, onClose, title = "Scan QR code", hint }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const doneRef = useRef(false);
  const [error, setError] = useState("");

  const stop = useCallback(() => {
    doneRef.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => {
        try { t.stop(); } catch {}
      });
      streamRef.current = null;
    }
  }, []);

  const handleResult = useCallback((text) => {
    if (doneRef.current) return;
    stop();
    onResult?.(text);
  }, [onResult, stop]);

  useEffect(() => {
    let cancelled = false;
    doneRef.current = false;

    const tick = () => {
      if (doneRef.current || cancelled) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w && h) {
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          try {
            const img = ctx.getImageData(0, 0, w, h);
            const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
            if (code && code.data) {
              handleResult(code.data);
              return;
            }
          } catch {
            /* frame not ready / cross-origin — keep scanning */
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Camera access isn't available on this device.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => { try { t.stop(); } catch {} });
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.setAttribute("playsinline", "true");
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        setError(
          err?.name === "NotAllowedError"
            ? "Camera permission was denied. Enable it in settings, or enter the code manually."
            : "Couldn't start the camera. Enter the code manually instead."
        );
      }
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [handleResult, stop]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(4,8,13,0.92)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div style={{ color: C.white, fontFamily: "Georgia,serif", fontWeight: 700, fontSize: 18, marginBottom: 10 }}>
        {title}
      </div>
      {hint && (
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 12, textAlign: "center", maxWidth: 320 }}>
          {hint}
        </div>
      )}

      {error ? (
        <div style={{ color: C.warning, fontSize: 13, textAlign: "center", maxWidth: 320, marginBottom: 16 }}>
          {error}
        </div>
      ) : (
        <div
          style={{
            position: "relative",
            width: "min(78vw, 320px)",
            aspectRatio: "1 / 1",
            borderRadius: 16,
            overflow: "hidden",
            border: `2px solid ${C.seafoam}`,
            background: "#000",
          }}
        >
          <video
            ref={videoRef}
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: "14%",
              border: `2px solid ${C.accent}`,
              borderRadius: 12,
              boxShadow: "0 0 0 100vmax rgba(0,0,0,0.25)",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />

      <button
        type="button"
        onClick={() => { stop(); onClose?.(); }}
        style={{
          marginTop: 22,
          background: "transparent",
          color: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 999,
          padding: "10px 22px",
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  );
}
